import { lazy, Suspense } from 'react';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginForm } from './features/auth/LoginForm';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navbar } from './components/Navbar';
import { LoadingFallback } from './components/LoadingFallback';
import { useAuth } from './hooks/useAuth';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

// Lazy load page-level components for code splitting
const EquipmentTabs = lazy(() =>
  import('./features/equipment/EquipmentTabs').then((module) => ({
    default: module.EquipmentTabs,
  }))
);

const Configuration = lazy(() => import('./pages/Configuration'));

const Reports = lazy(() =>
  import('./pages/Reports').then((module) => ({
    default: module.Reports,
  }))
);

const queryClient = new QueryClient();

function AppRoutes() {
  const { isAuthenticated, login, isLoggingIn } = useAuth();

  if (isAuthenticated) {
    return (
      <>
        <Navbar />
        <div style={{ padding: '20px' }}>
          <Suspense fallback={<LoadingFallback minHeight={400} />}>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <EquipmentTabs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuration"
                element={
                  <ProtectedRoute>
                    <Configuration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <div style={{ maxWidth: 400, margin: '100px auto' }}>
            <LoginForm onSubmit={login} isLoading={isLoggingIn} />
          </div>
        }
      />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider>
        <Notifications />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </MantineProvider>
    </QueryClientProvider>
  );
}

export default App;
